import React, { useEffect } from 'react'
import { Link } from 'wouter'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { SEOHead } from '@/components/seo-head'
import { useLinkedInConversions } from '@/lib/linkedin-conversions'

const B2BLeadsPage: React.FC = () => {
  const { trackPageView, trackButtonClick } = useLinkedInConversions()

  // Track page view on component mount
  useEffect(() => {
    trackPageView('b2b-leads')
  }, [trackPageView])

  const handleButtonClick = (buttonName: string) => {
    trackButtonClick(buttonName, 'b2b-leads')
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "B2B Leads & Qualified Prospects",
    "description": "Generate high-quality B2B leads through our RFP platform. Connect with qualified prospects actively seeking your services.",
    "url": "https://sourcedin.ai/b2b-leads",
    "mainEntity": {
      "@type": "Organization",
      "name": "SourcedIn",
      "url": "https://sourcedin.ai"
    }
  }

  return (
    <>
      <SEOHead
        title="B2B Leads & Qualified Prospects | SourcedIn RFP Platform"
        description="Generate high-quality B2B leads through our RFP platform. Connect with qualified prospects actively seeking your services through detailed RFPs."
        keywords="B2B leads, qualified prospects, RFP leads, business leads, vendor leads, procurement leads, B2B sales leads"
        canonical="https://sourcedin.ai/b2b-leads"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
        {/* Hero Section */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Generate High-Quality <span className="text-green-600">B2B Leads</span> Through RFPs
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Connect with qualified prospects who are actively seeking your services. Our RFP platform 
              provides you with warm leads that have already expressed interest in your industry.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-green-600 hover:bg-green-700" onClick={() => handleButtonClick('start-getting-leads')}>
                <Link to="/register">Start Getting Leads</Link>
              </Button>
              <Button asChild variant="outline" size="lg" onClick={() => handleButtonClick('learn-more')}>
                <Link to="/vendors">Learn More</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Lead Quality Section */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Why RFP Leads Are Higher Quality
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <CardTitle>Pre-Qualified Prospects</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>
                    Every lead comes from a detailed RFP with specific requirements, budget, and timeline. 
                    These prospects have already committed to making a purchase decision.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <CardTitle>Budget Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>
                    Know the budget range upfront. RFPs include budget information, helping you qualify 
                    leads and tailor your proposals accordingly.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <CardTitle>Clear Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>
                    Understand project timelines and deadlines. RFPs provide clear timelines, helping you 
                    prioritize leads and manage your sales pipeline effectively.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <CardTitle>Detailed Requirements</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>
                    Access comprehensive project requirements and specifications. This helps you prepare 
                    targeted proposals that address specific client needs.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <CardTitle>Decision Maker Access</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>
                    Connect directly with decision makers. RFPs are created by procurement teams and 
                    decision makers who have the authority to make purchasing decisions.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <CardTitle>Higher Conversion Rates</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>
                    RFP leads typically have 3-5x higher conversion rates compared to cold leads. 
                    These prospects are actively seeking solutions and ready to make decisions.
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Lead Generation Process */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              How Our B2B Lead Generation Works
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Browse RFPs</h3>
                <p className="text-gray-600">
                  Search and filter RFPs that match your services and expertise across various industries.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Review Details</h3>
                <p className="text-gray-600">
                  Access detailed project requirements, budget ranges, timelines, and company information.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Submit Proposal</h3>
                <p className="text-gray-600">
                  Create and submit compelling proposals directly through our platform with all necessary details.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-orange-600">4</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">Connect & Win</h3>
                <p className="text-gray-600">
                  Engage with prospects, answer questions, and close deals with qualified leads.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Success Metrics */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Proven Results for B2B Lead Generation
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">85%</div>
                <p className="text-gray-600">Higher lead quality compared to cold outreach</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">3.2x</div>
                <p className="text-gray-600">Better conversion rates than traditional methods</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">60%</div>
                <p className="text-gray-600">Faster sales cycle with pre-qualified prospects</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-600 mb-2">$50K+</div>
                <p className="text-gray-600">Average deal size from RFP leads</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Start Generating High-Quality B2B Leads Today
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Join thousands of vendors who are already using our platform to connect with qualified 
              prospects and grow their business through RFPs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-green-600 hover:bg-green-700">
                <Link to="/register">Get Started Free</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/contact">Schedule Demo</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default B2BLeadsPage
